import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Form,
    message,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Tooltip
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildSteps, extractLocalizedValue, getStatusColor } from "../../../common";
import { VerificationStatus, VerificationStep } from "../../../common/constant"
import {
    useAbandonUserVerificationProfile,
    useAssignUserVerificationProfile,
    useInitializeUserVerificationHistory,
    useSaveUserVerificationHistory,
    useSubmitUserVerificationHistory,
    useUpdateUserVerificationHistoryStatus
} from "../../../function/accountVerification";
import { debounce } from "lodash";
import { globalColorThemeState, globalNotificationState } from "../../../../../_common/global_state";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { convertToUppercase, useIsMobile } from "../../../../../_common/function";
import { initializeDocumentForm, renderFormFields } from "../../../common/form";
import { verificationRefetchState } from "../../../state";
import {
    CaretDownFilled,
    CheckCircleOutlined,
    CloseCircleFilled,
    CustomerServiceOutlined,
    FileOutlined,
    FileTextOutlined,
    FireOutlined,
    LockOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import { countryList_language } from "../../../../../../config/countryList_language";
import { isMobile } from "react-device-detect";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import {
    apiCancelVerificationHistory,
    apiDuplicateUserVerificationHistory
} from "../../../../../api/restful/verification";
import { apiGetQuestionnaireRecordStatus } from "../../../../../api/restful/questionnaire";
import { cleanStep } from "../../../common";

export default function AccountVerify_3(props) {
    const {
        userVerificationFlows,
        getUserVerificationFlows,
        userVerificationProfile,
        refetchUserVerificationProfile,
        userVerificationTiers,
        refetchUserVerificationTiers,
        userVerificationHistories,
        refetchUserVerificationHistories,
        accessRightsList
    } = props

    const { t } = useTranslation();

    const initialStep = userVerificationProfile?.flowSetting ? VerificationStep.Tier : VerificationStep.Flow;

    const { abandonUserVerificationProfile, loading: abandonLoading } = useAbandonUserVerificationProfile();
    const {
        updateUserVerificationHistoryStatus,
        loading: updateHistoryStatusLoading
    } = useUpdateUserVerificationHistoryStatus();

    const [currentStep, setCurrentStep] = useState(initialStep);
    const [selectedVerificationType, setSelectedVerificationType] = useState(null);
    const selectedFlow = userVerificationFlows?.find(flow => flow._id === userVerificationProfile?.flowSetting) || null;
    const [selectedTier, setSelectedTier] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedInnerDocument, setSelectedInnerDocument] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(userVerificationProfile?.country)
    const [cancelOpen, setCancelOpen] = useState(false);

    const sortedHistories = [...(userVerificationHistories || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    const currentHistory = sortedHistories?.find((history) => {
        const id = history.tierSetting?._id || history.tierSetting;
        return id === selectedTier?._id;
    });
    const statusHistory = currentHistory || sortedHistories?.[0] || null;
    const nextTierAvailableStatus = ["pending_verification", "verified"].includes(sortedHistories?.[0]?.status)
    const tierLevelArray = userVerificationTiers?.map(d => d?.tierLevel)
    const tierSettingNextTierExist = sortedHistories?.[0]?.tierLevel < Math.max(...tierLevelArray)

    const latestByTierLevel = sortedHistories.reduce((acc, h) => {
        if (!acc[h.tierLevel]) acc[h.tierLevel] = h;
        return acc;
    }, {});
    const failedTierHistory = Object.values(latestByTierLevel)
        .sort((a, b) => a.tierLevel - b.tierLevel)
        .find(h => h.status === "verification_failed");
    const blockedTierHistory = failedTierHistory
        ? Object.values(latestByTierLevel).find(h =>
            h.tierLevel > failedTierHistory.tierLevel && h.status === "draft_verification"
          )
        : null;
    const blockedByFailedTier = !!(failedTierHistory && blockedTierHistory);
    const activeDraftHistory = Object.values(latestByTierLevel).find(h => h.status === "draft_verification");

    const goNext = () => setCurrentStep(prev => prev + 1);
    const goTo = (step) => setCurrentStep(step);
    const goBack = () => setCurrentStep(prev => prev - 1);

    const handleSelectVerificationType = (type) => {
        setSelectedVerificationType(type);
    }

    const handleStartVerification = async () => {
        await Promise.all([
            refetchUserVerificationTiers(),
            refetchUserVerificationProfile(),
        ]);
    }

    const handleTryAgainFromStatus = async () => {
        try {
            if (!statusHistory?._id) {
                goTo(VerificationStep.Tier);
                return;
            }
            if (!["verification_failed", "canceled"].includes(statusHistory?.status)) {
                goTo(VerificationStep.Tier);
                return;
            }
            const result = await apiDuplicateUserVerificationHistory({ _id: statusHistory._id });
            if (result?.status !== "Success") {
                goTo(VerificationStep.Tier);
                return;
            }
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ]);
            const tierId = statusHistory?.tierSetting?._id || statusHistory?.tierSetting;
            const matchedTier = userVerificationTiers?.find(tier => tier?._id === tierId) || null;
            setSelectedTier(matchedTier);
            const timeout = setTimeout(() => {
                goTo(VerificationStep.Document);
            }, 150);
            return () => clearTimeout(timeout);
        } catch (e) {
            console.error("handleTryAgainFromStatus error:", e);
            goTo(VerificationStep.Tier);
        }
    };

    const handleStartOverFromStatus = async () => {
        try {
            if (statusHistory?._id) {
                await apiCancelVerificationHistory(statusHistory._id);
            }
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ]);
            setSelectedTier(null);
            setSelectedDocument(null);
            setSelectedInnerDocument(null);
            goTo(VerificationStep.Country);
        } catch (e) {
            console.error("handleStartOverFromStatus error:", e);
            goTo(VerificationStep.Country);
        }
    };

    const handleCancelFlow = () => {
        setCancelOpen(true)
    }

    const handleResubmitVerification = async () => {

        if (!currentHistory?._id || currentHistory.status !== VerificationStatus.PENDING) return;

        const payload = {
            _id: currentHistory._id,
            status: VerificationStatus.DRAFT
        }

        await updateUserVerificationHistoryStatus(payload)
        await Promise.all([
            refetchUserVerificationHistories(),
        ])
    }

    const buildBreadcrumbItems = () => {
        const items = [];

        if (selectedFlow) {
            items.push({
                title: extractLocalizedValue(selectedFlow?.displayName) || selectedFlow?.name,
            });
        }

        if (currentStep > VerificationStep.Tier && selectedTier) {

            if (currentStep === VerificationStep.Document) {
                const tierRequired = selectedTier?.numberOfRequired;
                const documentsRequired = selectedTier?.documentSettings?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name
                let message = ""

                if (tierRequired === selectedTier?.documentSettings?.length || tierRequired === documentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (tierRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", { numberOfRequired: tierRequired });
                } else {
                    message = ""
                }

                if (message) {
                    title = (
                        <span>
                            {title} (<strong>{message}</strong>)
                        </span>
                    );
                }

                items.push({ title });
            } else {
                items.push({ title: extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name, });
            }
        }

        if (currentStep > VerificationStep.Document && selectedDocument) {

            if (currentStep === VerificationStep.DocumentGroup) {
                const documentRequired = selectedDocument?.numberOfRequired;
                const innerDocumentsRequired = selectedDocument?.steps?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name
                let message = ""

                if (documentRequired === selectedDocument?.steps?.length || documentRequired === innerDocumentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (documentRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", { numberOfRequired: documentRequired });
                } else {
                    message = ""
                }

                if (message) {
                    title = (
                        <span>
                            {title} (<strong>{message}</strong>)
                        </span>
                    );
                }

                items.push({ title });
            } else if (selectedInnerDocument) {
                items.push({ title: extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name, });
            }
        }

        if (currentStep > VerificationStep.DocumentGroup && selectedDocument) {
            let title = extractLocalizedValue(selectedInnerDocument?.displayName || selectedDocument?.displayName) || selectedInnerDocument?.name || selectedDocument?.name;

            const verificationStatus = currentHistory?.status;

            items.push({
                title: (
                    <span>
                        {title}
                        {verificationStatus !== VerificationStatus.DRAFT && (
                            <Tag
                                color={getStatusColor(verificationStatus)}
                                style={{ marginLeft: 6 }}
                            >
                                {convertToUppercase(verificationStatus.replace(/_/g, " "))}
                            </Tag>
                        )}
                    </span>
                ),
            });
        }

        return items;
    }

    useEffect(() => {
        const statusList = userVerificationHistories?.map(d => d?.status)

        if (blockedByFailedTier) {
            setCurrentStep(VerificationStep.Tier);
        } else if (nextTierAvailableStatus && tierSettingNextTierExist) {
            setCurrentStep(VerificationStep.Tier);
        } else if (activeDraftHistory) {
            const draftTierSettingId = activeDraftHistory.tierSetting?._id || activeDraftHistory.tierSetting;
            setSelectedTier(userVerificationTiers?.find(d => d?._id === draftTierSettingId));
            setCurrentStep(VerificationStep.Document);
        } else if (statusList?.includes("pending_verification")) {
            setCurrentStep(VerificationStep.Status);
        } else if (userVerificationProfile?.flowSetting) {
            setCurrentStep(VerificationStep.Tier);
        } else if (userVerificationProfile?.status === "not_verified") {
            setCurrentStep(VerificationStep.Country);
        } else {
            setCurrentStep(VerificationStep.Flow);
        }
    }, [userVerificationProfile, userVerificationTiers])

    const breadcrumbItems = buildBreadcrumbItems(
        currentStep,
        selectedFlow,
        selectedTier,
        selectedDocument,
        extractLocalizedValue
    );

    return (
        <div id={"accountVerify"}>
            <Row justify={"center"} style={{ marginBottom: "60px" }}>
                <div style={{ width: "100%" }}>
                    <Space style={{ width: "100%" }} size={24} direction="vertical">

                        {
                            currentStep > VerificationStep.Flow && currentStep < VerificationStep.Status && (
                                <div className="breadcrumb-header">
                                    <div className="breadcrumb-container">
                                        <Breadcrumb
                                            items={breadcrumbItems}
                                        />
                                    </div>

                                    <div className="breadcrumb-action">

                                        {currentStep >= VerificationStep.Tier && currentHistory?.status === "draft_verification" && (
                                            <Button
                                                className="breadcrumb-action abandon-btn"
                                                onClick={handleCancelFlow}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                        )}

                                        {currentStep === VerificationStep.Document &&
                                            currentHistory?.status === VerificationStatus.PENDING && (
                                                <Button
                                                    danger
                                                    className="breadcrumb-action"
                                                    onClick={handleResubmitVerification}
                                                >
                                                    {t("accountVerification.resubmitDocument")}
                                                </Button>
                                            )
                                        }

                                    </div>

                                </div>
                            )
                        }

                        {
                            currentStep === VerificationStep.Country
                            &&
                            (
                                <SelectCountry
                                    selectedCountry={selectedCountry}
                                    setSelectedCountry={setSelectedCountry}
                                    userVerificationProfile={userVerificationProfile}
                                    goNext={goNext}
                                    getUserVerificationFlows={getUserVerificationFlows}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Flow
                            &&
                            (
                                <SelectFlow
                                    userVerificationFlows={userVerificationFlows}
                                    selectedVerificationType={selectedVerificationType}
                                    handleSelectVerificationType={handleSelectVerificationType}
                                    handleStartVerification={handleStartVerification}
                                    goBack={goBack}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Tier
                            &&
                            (
                                <SelectTier
                                    userVerificationHistories={userVerificationHistories}
                                    userVerificationTiers={userVerificationTiers}
                                    refetchUserVerificationProfile={refetchUserVerificationProfile}
                                    goTo={goTo}
                                    setSelectedTier={setSelectedTier}
                                    accessRightsListCategory={accessRightsList}
                                    blockedByFailedTier={blockedByFailedTier}
                                    failedTierHistory={failedTierHistory}
                                    blockedTierHistory={blockedTierHistory}
                                />
                            )
                        }

                        {
                            (currentStep === VerificationStep.Document || currentStep === VerificationStep.DocumentGroup) &&
                            (
                                <SelectDocument
                                    currentHistory={currentHistory}
                                    setSelectedDocument={setSelectedDocument}
                                    setSelectedInnerDocument={setSelectedInnerDocument}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    userVerificationHistories={userVerificationHistories}
                                    goNext={goNext}
                                    goBack={goBack}
                                    goTo={goTo}
                                    isInnerDocument={currentStep === VerificationStep.DocumentGroup}
                                    tierSettingNextTierExist={tierSettingNextTierExist}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Form
                            &&
                            (
                                <DocumentForm
                                    currentHistory={currentHistory}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    selectedInnerDocument={selectedInnerDocument}
                                    goBack={goBack}
                                    goTo={goTo}
                                    refetchUserVerificationHistories={refetchUserVerificationHistories}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Status &&
                            <VerificationStatusScreen
                                statusHistory={statusHistory}
                                onTryAgain={handleTryAgainFromStatus}
                                onStartOver={handleStartOverFromStatus}
                                userVerificationTiers={userVerificationTiers}
                            />
                        }
                        {/* <Row justify="space-between" style={{ marginTop: 24 }}>
                            {currentStep > 0 && (
                                <Button onClick={goBack}>
                                    Back
                                </Button>
                            )}

                            {currentStep < 3 && (
                                <Button type="primary" onClick={goNext}>
                                    Next
                                </Button>
                            )}
                        </Row> */}
                    </Space>
                </div>
            </Row>

            <Modal
                title={"Confirm Cancel"}
                cancelText={t("No, Continue")}
                centered={true}
                open={cancelOpen}
                getContainer={() => document.getElementById("layout-3")}
                width={isMobile ? "90%" : "350px"}
                okButtonProps={{ type: "default" }}
                onOk={async () => {
                    await apiCancelVerificationHistory(currentHistory?._id);
                    await refetchUserVerificationProfile();
                    await refetchUserVerificationHistories();
                    setCancelOpen(false)

                    setSelectedTier(null)
                    setSelectedDocument(null)
                    setSelectedDocument(null)
                    goTo(VerificationStep.Country)
                }}
                okText={"Yes, Cancel"}
                cancelButtonProps={{ type: "primary" }}
                onCancel={() => setCancelOpen(false)}
            >
                Are you sure you want to cancel this verification process? Your progress will be lost.
            </Modal>
        </div>
    )
}

function SelectCountry(props) {
    const { selectedCountry, setSelectedCountry, userVerificationProfile, goNext, getUserVerificationFlows } = props

    const { t } = useTranslation();
    const { assignUserVerificationProfile } = useAssignUserVerificationProfile();
    const isMobile = useIsMobile();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const handleSelectCountry = () => {
        if (userVerificationProfile?.country && selectedCountry !== userVerificationProfile?.country) {
            openModal()
        } else {
            goNext()
        }
    }

    const handleChangeCountryFlow = async () => {
        await getUserVerificationFlows({ country: selectedCountry });
        goNext()
    }

    return (
        <>
            <Card bordered={false} className={"shadow-card"}>
                <Space style={{ width: "100%" }} size={16} direction="vertical">
                    <div className={"mb-4"}>
                        <h6><strong>{t("accountVerification.selectCountry")}</strong></h6>
                        <p className={"secondary-text"}>{t("accountVerification.note.selectCountry")}</p>
                    </div>

                    <Row gutter={16} align={"middle"}>
                        <Col flex={1}>
                            <Select
                                style={{ width: "100%" }}
                                showSearch
                                placeholder={t("accountVerification.placeholder.selectCountry")}
                                optionFilterProp={"label"}
                                value={selectedCountry}
                                onChange={(country) => setSelectedCountry(country)}
                                suffixIcon={<CaretDownFilled style={{ pointerEvents: "none" }} />}
                                options={countryList_language?.map(d => ({
                                    ...d,
                                    label: t(d?.label),
                                }))}
                            />
                        </Col>
                        <Col {...(isMobile ? { span: 24 } : { flex: "50px" })}>
                            <Button
                                block
                                type="primary"
                                disabled={!selectedCountry}
                                onClick={handleSelectCountry}
                                className={isMobile ? "mt-4" : undefined}
                            >
                                {t("common.next")}
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </Card>

            <Modal
                className="verification-modal"
                title={t("Change Country")}
                open={isModalOpen}
                onCancel={closeModal}
                destroyOnClose
                width={"400px"}
                getContainer="#layout-3"
                footer={
                    <Row justify={"end"} gutter={16}>
                        <Col>
                            <Button onClick={closeModal}>{t("common.cancel")}</Button>
                        </Col>
                        <Col>
                            <Button type="primary" onClick={handleChangeCountryFlow}>
                                {t("common.confirm")}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div>{t("The selected country does not match your profile. Do you wish to continue?")}</div>
            </Modal>
        </>
    );
}

function SelectFlow(props) {
    const {
        userVerificationFlows,
        selectedVerificationType,
        handleSelectVerificationType,
        handleStartVerification,
        goBack
    } = props

    const { t } = useTranslation();
    const colorState = useRecoilValue(globalColorThemeState);
    const { assignUserVerificationProfile } = useAssignUserVerificationProfile();
    const { refetchUserVerificationHistories, refetchUserVerificationProfile } = useRecoilValue(verificationRefetchState)

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const confirmStartVerification = async () => {
        try {
            if (selectedVerificationType) {
                const selectedFlow = userVerificationFlows?.find(flow => flow.verificationType === selectedVerificationType);


                if (!selectedFlow._id) {
                    return;
                }

                const result = await assignUserVerificationProfile(selectedFlow._id);

                if (result?.status === "Success") {
                    handleStartVerification(selectedVerificationType);
                }
                // else if (result?.status === "failed") {
                //     setNotificationMessage({
                //         status: "error",
                //         title: "",
                //         msg: "Apologies, we are currently undergoing maintenance for the registration page. We're actively working to resolve the issue.",
                //         key: "systemNotification",
                //     });
                // }
            }
        } catch (error) {
            console.error("failed to start verification: ", error)
        } finally {
            closeModal();
        }
    };

    return (
        <>
            <Card bordered={false} className={"shadow-card"}>
                <Space style={{ width: "100%" }} size={16} direction="vertical">
                    <h6 className="heading">
                        {t("accountVerification.note.selectVerificationType")}
                    </h6>

                    {[...(userVerificationFlows || [])]
                        .sort((a, b) => (a.verificationType === "individual" ? -1 : b.verificationType === "individual" ? 1 : 0))
                        .map((flow) => {
                            const displayName = extractLocalizedValue(flow.displayName) || flow.name;
                            const description = extractLocalizedValue(flow.description);
                            // const logo = flow?.[colorState]?.trim() || "";

                            const isSelected = selectedVerificationType === flow.verificationType;

                            return (
                                <Card
                                    key={flow._id}
                                    onClick={() => handleSelectVerificationType(flow.verificationType)}
                                    className={`account-verification-card-selection verification-card ${isSelected ? "selected" : ""}`}
                                    bodyStyle={{ padding: "16px" }}
                                >
                                    <div className={"selection-content-box"}>
                                        {/*{logo && (*/}
                                        {/*    <img*/}
                                        {/*        src={logo}*/}
                                        {/*        alt={displayName}*/}
                                        {/*    />*/}
                                        {/*)}*/}

                                        <Tag className="verification-card-type align-self-start mt-1"
                                            color={flow.verificationType === "individual" ? "geekblue" : "lime"}>
                                            {t(`user.statusTag.${flow.verificationType}`)}
                                        </Tag>

                                        <div className={"mt-1 ms-1 md:mt-0 md:ms-0"}
                                            style={{ flex: 1, textAlign: "left" }}>
                                            <div className="account-verification-card-selection-title">
                                                {displayName}
                                            </div>

                                            {
                                                description &&
                                                <p className="account-verification-card-selection-description">
                                                    {description}
                                                </p>
                                            }
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}

                    {/* Start verification */}
                    <Row gutter={8} className={"mt-6"}>
                        <Col span={12}>
                            <Button block onClick={goBack}>
                                {t("common.back")}
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button
                                type="primary"
                                block
                                disabled={!selectedVerificationType}
                                onClick={openModal}
                            >
                                {t("accountVerification.startVerification")}
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </Card>

            {/* Confirmation modal */}
            <Modal
                className="verification-modal"
                title={t("accountVerification.startVerification")}
                open={isModalOpen}
                onCancel={closeModal}
                destroyOnClose
                centered
                width={"400px"}
                footer={
                    < Row justify={"end"} gutter={16}>
                        <Col>
                            <Button onClick={closeModal}>{t("common.cancel")}</Button>
                        </Col>
                        <Col>
                            <Button type="primary" onClick={confirmStartVerification}>
                                {t("accountVerification.startVerificationProcess")}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div>{t("accountVerification.startVerificationDescription")}</div>
            </Modal>
        </>
    );
}

function SelectTier(props) {
    const {
        userVerificationHistories,
        goTo,
        setSelectedTier,
        userVerificationTiers,
        accessRightsListCategory,
        blockedByFailedTier,
        failedTierHistory,
        blockedTierHistory,
    } = props

    const { t } = useTranslation();
    const isMobile = useIsMobile()
    const { initializeUserVerificationHistory } = useInitializeUserVerificationHistory();
    const {
        refetchUserVerificationProfile,
        refetchUserVerificationHistories,
    } = useRecoilValue(verificationRefetchState);
    const [isCancelling, setIsCancelling] = useState(false)

    const handleCancelBlockedTier = async () => {
        if (!blockedTierHistory?._id) return;
        try {
            setIsCancelling(true);
            await apiCancelVerificationHistory(blockedTierHistory._id);
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ]);
        } catch (e) {
            console.error("handleCancelBlockedTier error:", e);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSelectTier = async (tier, startNewRecord = false) => {
        setSelectedTier(tier);
        const existingHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
        const recordExist = ["canceled", "verification_failed"].includes(existingHistory?.status)

        if (!existingHistory?._id || (recordExist && startNewRecord)) {
            try {
                const result = await initializeUserVerificationHistory(tier._id);

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    if (startNewRecord) return

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }

            } catch (error) {
                console.error("error initializing verification history:", error);
            }
        } else if (recordExist) {
            try {
                const result = await apiDuplicateUserVerificationHistory({ _id: existingHistory?._id })

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }
            } catch (e) {
                console.error("error duplicating verification history:", e);
            }
        } else {
            goTo(VerificationStep.Document);
        }
    }

    const getTierStatus = (tierId) => {
        const history = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .find((h) => (h.tierSetting?._id || h.tierSetting) === tierId);
        return history?.status ?? null;
    }

    const getNextTier = (history, tiers) => {
        const sortedHistory = [...(history || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        const validHistory = sortedHistory.find(h => h.status === "pending_verification" || h.status === "verified")
        if (!validHistory) return tiers.find(t => t.tierLevel === 1)

        let nextTierLevel = validHistory.tierLevel + 1

        while (true) {
            const nextTier = tiers.find(t => t.tierLevel === nextTierLevel)
            if (!nextTier) return null

            const nextTierHistory = sortedHistory.find(h => h.tierLevel === nextTierLevel)

            if (
                !nextTierHistory ||
                (nextTierHistory.status !== "pending_verification" && nextTierHistory.status !== "verified")
            ) {
                return nextTier
            }

            nextTierLevel++
        }
    }

    const currentTierAvailable = getNextTier(userVerificationHistories, userVerificationTiers)
    const currentTierAvailableId = currentTierAvailable?._id
    const currentTierLevel = currentTierAvailable?.tierLevel || 0

    const standaloneTier = userVerificationTiers?.find(d => d?.type === "standalone")

    const mapCategoryAccess = (category, access) => {
        const result = {}

        for (const [catKey, catValues] of Object.entries(category || {})) {
            const arr = []

            for (const [accKey, accVal] of access) {
                if (catValues[accKey]) {
                    arr.push([accKey, accVal])
                }
            }

            if (arr.length > 0) {
                result[catKey] = arr
            }
        }

        return result
    }

    if (blockedByFailedTier) {
        const blockedTierSettingId = blockedTierHistory?.tierSetting?._id || blockedTierHistory?.tierSetting;
        const failedTierSettingId = failedTierHistory?.tierSetting?._id || failedTierHistory?.tierSetting;
        const blockedTier = userVerificationTiers?.find(t => t._id === blockedTierSettingId) || (blockedTierHistory?.tierSetting?._id ? blockedTierHistory.tierSetting : null);
        const failedTier = userVerificationTiers?.find(t => t._id === failedTierSettingId) || (failedTierHistory?.tierSetting?._id ? failedTierHistory.tierSetting : null);
        const blockedTierName = extractLocalizedValue(blockedTier?.displayName) || blockedTier?.name;
        const failedTierName = extractLocalizedValue(failedTier?.displayName) || failedTier?.name;

        return (
            <Row gutter={[16, 16]}>
                {blockedTier && (
                    <Col span={24}>
                        <Card className="account-verification-card-selection h-full">
                            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                {isMobile && (
                                    <Tag color={getStatusColor(blockedTierHistory.status)} bordered={false}>
                                        {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                    </Tag>
                                )}
                                <Row justify="space-between" align="middle">
                                    <div className="account-verification-card-selection-title">{blockedTierName}</div>
                                    <Space className="ms-auto">
                                        {blockedTier?.tierLevel && (
                                            <Tag bordered={false} color="geekblue">{t("Tier")} {blockedTier.tierLevel}</Tag>
                                        )}
                                        {!isMobile && (
                                            <Tag color={getStatusColor(blockedTierHistory.status)} bordered={false}>
                                                {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                            </Tag>
                                        )}
                                    </Space>
                                </Row>
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={`Tier ${failedTierHistory.tierLevel} (${failedTierName}) was rejected. Cancel this Tier ${blockedTierHistory.tierLevel} verification first to fix it.`}
                                />
                                <Row className="mt-2">
                                    <Col xs={24} md={12}>
                                        <Button
                                            danger
                                            block={isMobile}
                                            loading={isCancelling}
                                            onClick={handleCancelBlockedTier}
                                        >
                                            {t("Cancel Tier")} {blockedTierHistory.tierLevel} {t("Verification")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Space>
                        </Card>
                    </Col>
                )}
                {failedTier && (
                    <Col span={24}>
                        <Tooltip title={`Cancel Tier ${blockedTierHistory.tierLevel} first to proceed`}>
                            <Card className="account-verification-card-selection h-full" style={{ opacity: 0.6 }}>
                                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                    {isMobile && (
                                        <Tag color={getStatusColor(failedTierHistory.status)} bordered={false}>
                                            {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                        </Tag>
                                    )}
                                    <Row justify="space-between" align="middle">
                                        <div className="account-verification-card-selection-title">{failedTierName}</div>
                                        <Space className="ms-auto">
                                            {failedTier?.tierLevel && (
                                                <Tag bordered={false} color="geekblue">{t("Tier")} {failedTier.tierLevel}</Tag>
                                            )}
                                            {!isMobile && (
                                                <Tag color={getStatusColor(failedTierHistory.status)} bordered={false}>
                                                    {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                                </Tag>
                                            )}
                                        </Space>
                                    </Row>
                                    <Space size={6}>
                                        <LockOutlined />
                                        <span className="secondary-text">
                                            {`Cancel Tier ${blockedTierHistory.tierLevel} first to fix this`}
                                        </span>
                                    </Space>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                )}
            </Row>
        );
    }

    return (
        <Row gutter={[16, 16]}>
            {(standaloneTier ? [standaloneTier] : (currentTierAvailable ? [currentTierAvailable] : []))?.map((tier) => {
                const displayName = extractLocalizedValue(tier.displayName) || tier.name;
                const description = extractLocalizedValue(tier.description);
                const status = getTierStatus(tier._id);
                const tierVerificationHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id);
                const displayStatus = status && tierVerificationHistory?.documents !== null;
                const documents = tier?.documentSettings?.map(doc => extractLocalizedValue(doc?.documentSettingDoc?.displayName) || doc?.documentSettingDoc?.name)

                const accessRightsList = Object.entries(tier?.accessRights || {}) || []
                const access = accessRightsList.filter(item => item[1] === null)
                const accessWithAmounts = accessRightsList.filter(item => item[1] !== null)
                const nextButtonAvailable = (!currentTierAvailableId && (tier?.tierLevel <= 1 || tier?.type === "standalone")) || tier?._id === currentTierAvailableId
                const accessWithCategory = mapCategoryAccess(accessRightsListCategory, access);

                return (
                    <Col className={"mh-100"} span={24} key={tier._id}>
                        <Tooltip placement="top">
                            <Card className="account-verification-card-selection h-full">
                                <Space direction="vertical" size={8} style={{ width: "100%", height: "100%" }}>
                                    {displayStatus && isMobile ? (
                                        <Tag color={getStatusColor(status)} bordered={false}>
                                            {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                        </Tag>
                                    ) : <></>}

                                    <Row justify={(displayStatus && !isMobile) ? "space-between" : "start"} align="middle">
                                        <div className="account-verification-card-selection-title">{displayName}</div>
                                        <Space className="ms-auto">
                                            {tier?.tierLevel ? (
                                                <Tag className="ms-1" bordered={false} color="geekblue">{t("Tier")} {tier?.tierLevel}</Tag>
                                            ) : ""}
                                            {displayStatus && !isMobile ? (
                                                <Tag color={getStatusColor(status)} bordered={false}>
                                                    {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                                </Tag>
                                            ) : <></>}
                                        </Space>
                                    </Row>

                                    <p><small>{description}</small></p>

                                    <Row gutter={[8, 16]}>
                                        {documents?.length > 0 && (
                                            <Col span={isMobile ? 24 : 12}>
                                                <p className="mb-2 text-xs tier-section-label">Documents Required</p>
                                                <Space direction="vertical" wrap size={4}>
                                                    {documents?.map(d => (
                                                        <div className="documents-tag" key={d}>
                                                            <FileOutlined className="text-md" /> {d}
                                                        </div>
                                                    ))}
                                                </Space>
                                                {isMobile && <Divider className="my-2" />}

                                                {nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className="mt-4" gutter={[8, 8]} onClick={(e) => e.stopPropagation()}>
                                                        <Col xs={24} md={14}>
                                                            <Button type="primary" block onClick={() => handleSelectTier(tier)}>
                                                                {t("Continue with last record")}
                                                            </Button>
                                                        </Col>
                                                        <Col xs={24} md={9}>
                                                            <Button block onClick={() => handleSelectTier(tier, true)}>
                                                                {t("Start New Record")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                                {nextButtonAvailable && !["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className="mt-4" gutter={[8, 8]}>
                                                        <Col span={22}>
                                                            <Button type="primary" block onClick={() => handleSelectTier(tier)}>
                                                                {t("Proceed")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </Col>
                                        )}

                                        <Col span={isMobile ? 24 : (documents?.length > 0 ? 12 : 24)}>
                                            {accessWithAmounts?.filter(([_, { dailyMax, allTimeMax }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                <>
                                                    <p className="tier-limits-inline-title">Account Limits After Verification</p>
                                                    <Space direction="vertical" size={0} style={{ width: "100%" }}>
                                                        {accessWithAmounts
                                                            ?.filter(([_, { dailyMax, allTimeMax }]) => dailyMax > 0 || allTimeMax > 0)
                                                            ?.map(([module, { dailyMax, allTimeMax }], idx) => (
                                                                <Row key={module} justify="space-between" align="middle"
                                                                    className={`verification-limit-row${idx > 0 ? " verification-limit-row-divider" : ""}`}>
                                                                    <Space size={8}>
                                                                        <LockOutlined className="verification-limit-icon" />
                                                                        <span className="tier-limit-label">
                                                                            {t(`accountVerification.tierSettingAccessRight.${module}`, module.replace(/_/g, " "))}
                                                                        </span>
                                                                    </Space>
                                                                    <span className="verification-limit-value">
                                                                        {dailyMax > 0
                                                                            ? `${Number(dailyMax).toLocaleString()} USD Daily`
                                                                            : allTimeMax > 0 && allTimeMax !== -1
                                                                                ? `${Number(allTimeMax).toLocaleString()} USD`
                                                                                : "Unlimited"}
                                                                    </span>
                                                                </Row>
                                                            ))}
                                                    </Space>
                                                </>
                                            )}
                                            {Object.keys(accessWithCategory).length > 0 && (
                                                <>
                                                    {accessWithAmounts?.filter(([_, { dailyMax, allTimeMax }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                        <Divider className="my-2" />
                                                    )}
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Collapse
                                                            expandIconPosition="end"
                                                            ghost
                                                            size="small"
                                                            items={[{
                                                                key: "features",
                                                                label: (
                                                                    <Space align="center">
                                                                        <SafetyCertificateOutlined className="text-md" />
                                                                        <span className="text-xs">Features Unlocked</span>
                                                                    </Space>
                                                                ),
                                                                children: (
                                                                    <div className="tier-modules-compact">
                                                                        {Object.values(accessWithCategory).flat().map(([module]) => (
                                                                            <span className="tier-module-chip" key={module}>
                                                                                <CheckCircleOutlined />
                                                                                {t(`accountVerification.tierSettingAccessRight.${module}`)}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ),
                                                            }]}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                );
            })}
        </Row>
    )
}

function SelectDocument(props) {
    const {
        currentHistory,
        selectedTier,
        setSelectedDocument,
        setSelectedInnerDocument,
        selectedDocument,
        isInnerDocument,
        goNext,
        goBack,
        goTo,
        userVerificationHistories,
        tierSettingNextTierExist
    } = props

    const isMobile = useIsMobile()
    const { t } = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const { submitUserVerificationHistory } = useSubmitUserVerificationHistory();
    const { refetchUserVerificationHistories } = useRecoilValue(verificationRefetchState);

    const isPendingVerification = currentHistory?.status === "pending_verification"

    const tierDocuments = isInnerDocument ? selectedDocument?.steps?.map(setting => ({
        ...setting.sourceDoc,
        isRequired: setting.isRequired,
    })) : selectedTier?.documentSettings?.map(
        setting => ({
            ...setting.documentSettingDoc,
            isRequired: setting.isRequired,
        })
    ) || [];

    const handleSelectDocument = (document) => {
        if (isInnerDocument) {
            setSelectedInnerDocument(document)
            goNext()
        } else {
            setSelectedInnerDocument(null)
            setSelectedDocument(document)
            goNext()

            if (document?.steps?.[0]?.sourceType === "field_setting") {
                goNext()
            }
        }
    }

    const handleSubmitVerification = async () => {
        if (!currentHistory?._id) return;

        if (selectedTier?.documentSettings) {
            for (const docSetting of selectedTier.documentSettings) {
                const docDef = docSetting.documentSettingDoc;
                if (!docDef?.steps) continue;

                const docHistory = currentHistory.documents?.find(d => d._id === docDef._id);

                for (const step of docDef.steps) {
                    if (step.isRequired && step.sourceDoc?.type === 'questionnaire') {
                        const stepHistory = docHistory?.steps?.find(s => s.sourceId === step.sourceDoc._id);
                        const recordId = stepHistory?.value;
                        const fieldName = extractLocalizedValue(step.sourceDoc.displayName) || step.sourceDoc.name;

                        if (!recordId) {
                            message.error(t("errorMsg.required.pleaseFillIn", { field: fieldName }));
                            return;
                        }

                        try {
                            const response = await apiGetQuestionnaireRecordStatus(recordId);
                            if (response?.status === 'success') {
                                const isSubmitted = response.data;
                                if (!isSubmitted) {
                                    message.error(`${t("Please submit the questionnaire first")}`);
                                    return;
                                }
                            }
                        } catch (error) {
                            console.error("Error checking questionnaire status", error);
                        }
                    }
                }
            }
        }

        try {
            const input = {
                _id: currentHistory._id,
                documents: currentHistory.documents?.map(doc => ({
                    ...doc,
                    steps: doc.steps?.map(cleanStep)
                }))
            }

            const result = await submitUserVerificationHistory(input)

            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.submitSuccessfully"),
                    key: "accountVerificationNotification",
                });

                if (tierSettingNextTierExist) {
                    goTo(VerificationStep.Tier)
                } else {
                    goTo(VerificationStep.Status)
                }
            }
        } catch (error) {
            console.error("handleSubmitTier: ", error)
            message.error(t("accountVerification.systemError"))
        }
    }

    return (
        <Row gutter={[16, 16]}>
            {tierDocuments.map((document) => {
                const displayName = extractLocalizedValue(document.displayName) || document.name;
                const description = extractLocalizedValue(document.description);

                const tierHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(
                    (h) => h.tierSetting === selectedTier._id
                );

                const submittedDoc = tierHistory?.documents?.find(
                    (d) => d._id === document._id
                );

                const isSubmittedInnerDoc = isInnerDocument ? tierHistory?.documents?.find(
                    (d) => d._id === selectedDocument?._id
                )?.steps?.find(d => d?.sourceId === document._id)?.value : null

                const status = (submittedDoc || isSubmittedInnerDoc) ? tierHistory.status : null;
                const color = status ? getStatusColor(status) : undefined

                return (
                    <Col span={isMobile ? 24 : 12} key={document._id} className={"mh-100"}>
                        <Card className={`account-verification-card-selection h-full`}>
                            <div className="account-verification-card-selection-title">
                                {
                                    document.isRequired
                                    &&
                                    (
                                        <Tooltip title={"This section is required"}>
                                            <span className="account-verification-required-mark">
                                                *
                                            </span>
                                        </Tooltip>
                                    )
                                }
                                {displayName}
                                {
                                    status ? <Tag className={"ms-2"} color={color}
                                        bordered={false}>{convertToUppercase(status.replace(/_/g, " "))}</Tag> : <></>
                                }
                            </div>

                            <p className={"mb-2"}><small>{description}</small></p>

                            <Row justify={"end"} className={"mt-auto"}>
                                <Button
                                    type={"default"}
                                    className="statusButton"
                                    onClick={() => handleSelectDocument(document)}
                                //loading={buttonState[document?.document?._id] || false}
                                >
                                    {t("common.next")}
                                </Button>
                            </Row>
                        </Card>
                    </Col>
                );
            })}

            <Row justify={"end"} className={"w-full mt-6"}>
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={goBack}>
                        Back
                    </Button>

                    {
                        !isPendingVerification
                        &&
                        (
                            <Button
                                type="primary"
                                onClick={handleSubmitVerification}
                                disabled={isPendingVerification}
                            >
                                Submit
                            </Button>
                        )
                    }
                </Space>
            </Row>
        </Row>
    )
}

function DocumentForm(props) {
    const {
        currentHistory,
        selectedTier,
        selectedDocument,
        selectedInnerDocument,
        goBack,
        goTo,
        refetchUserVerificationHistories
    } = props

    const { t } = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const { submitUserVerificationHistory, loading: submitLoading } = useSubmitUserVerificationHistory();
    const { saveUserVerificationHistory, loading: saveLoading } = useSaveUserVerificationHistory();

    const [form] = Form.useForm();
    const [saveStatus, setSaveStatus] = useState("idle"); // for label
    const [isSaving, setIsSaving] = useState(false); // for button

    const isPendingVerification = currentHistory?.status === VerificationStatus.PENDING

    const fields = (selectedInnerDocument || selectedDocument)?.steps?.map((step) => ({
        ...step.sourceDoc,
        isRequired: step.isRequired,
        ...(step.sourceDoc?.type === 'questionnaire' && { formSettingId: step.questionnaireId })
    })) || [];

    // const handleSubmit = async (formValues) => {
    //     if (!currentHistory?._id || !selectedDocument?._id) return;

    //     const updatedDoc = {
    //         _id: selectedDocument._id,
    //         steps: buildSteps(formValues, selectedDocument.steps),
    //     };

    //     const mergedDocuments = [...(currentHistory.documents || [])];
    //     const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);

    //     if (index >= 0) {
    //         mergedDocuments[index] = updatedDoc;
    //     } else {
    //         mergedDocuments.push(updatedDoc);
    //     }

    //     const payload = {
    //         _id: currentHistory._id,
    //         documents: mergedDocuments,

    //     };

    //     await submitUserVerificationHistory(payload)
    // }

    const debouncedSave = useRef(
        debounce(async (payload) => {
            try {
                setIsSaving(true)
                await saveUserVerificationHistory(payload);
                setSaveStatus("saved");
            } catch {
                setSaveStatus("idle");
            } finally {
                setIsSaving(false)
                setTimeout(() => setSaveStatus("idle"), 2000);
                await refetchUserVerificationHistories();
            }
        }, 1500)
    ).current;

    const handleAutoSave = useCallback(async () => {
        if (!currentHistory?._id || !selectedDocument?._id) return;

        const formValues = form.getFieldsValue();
        let innerDocuments

        let updatedDoc = {
            _id: selectedDocument._id,
            steps: buildSteps(formValues, selectedDocument.steps),
        };

        if (selectedInnerDocument) {
            innerDocuments = {
                _id: selectedInnerDocument._id,
                steps: buildSteps(formValues, selectedInnerDocument.steps),
            }

            updatedDoc = {
                _id: selectedDocument._id,
                steps: updatedDoc.steps.map((step) => {
                    if (step.sourceId === innerDocuments._id) {
                        return {
                            ...step,
                            value: { steps: innerDocuments.steps }
                        }
                    }

                    const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                    const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)

                    const isDocEmpty = !(foundInnerDoc?.value?.steps?.filter(d => d?.value)?.length)

                    return isDocEmpty ? { sourceId: step.sourceId, value: null } : foundInnerDoc ? {
                        ...foundInnerDoc,
                        sourceType: step.sourceType
                    } : step
                })
            }
        }

        const mergedDocuments = [...(currentHistory.documents || [])];
        const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
        if (index >= 0) mergedDocuments[index] = updatedDoc;
        else mergedDocuments.push(updatedDoc);

        const payload = {
            _id: currentHistory._id,
            documents: mergedDocuments.map(doc => ({
                ...doc,
                steps: doc.steps?.map(cleanStep)
            }))
        };

        setIsSaving(true);
        setSaveStatus("saving");

        debouncedSave(payload);
    }, [currentHistory._id, currentHistory.documents, selectedDocument._id, selectedDocument.steps, form, debouncedSave]);

    const handleSaveDocument = async () => {

        try {
            if (!currentHistory?._id || !selectedDocument?._id) return;

            const formValues = form.getFieldsValue();
            let innerDocuments

            let updatedDoc = {
                _id: selectedDocument._id,
                steps: buildSteps(formValues, selectedDocument.steps),
            };

            if (selectedInnerDocument) {
                innerDocuments = {
                    _id: selectedInnerDocument._id,
                    steps: buildSteps(formValues, selectedInnerDocument.steps),
                }

                updatedDoc = {
                    _id: selectedDocument._id,
                    steps: updatedDoc.steps.map((step) => {
                        if (step.sourceId === innerDocuments._id) {
                            return {
                                ...step,
                                value: { steps: innerDocuments.steps }
                            }
                        }

                        const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                        const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)

                        const isDocEmpty = !(foundInnerDoc?.value?.steps?.filter(d => d?.value)?.length)

                        return isDocEmpty ? { sourceId: step.sourceId, value: null } : foundInnerDoc ? {
                            ...foundInnerDoc,
                            sourceType: step.sourceType
                        } : step
                    })
                }
            }

            const mergedDocuments = [...(currentHistory.documents || [])];
            const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
            if (index >= 0) mergedDocuments[index] = updatedDoc;
            else mergedDocuments.push(updatedDoc);

            const payload = {
                _id: currentHistory._id,
                documents: mergedDocuments.map(doc => ({
                    ...doc,
                    steps: doc.steps?.map(cleanStep)
                }))
            };

            const result = await saveUserVerificationHistory(payload);


            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.saveSuccessfully"),
                    key: "accountVerificationNotification",
                });

                goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document);
            }

        } catch (error) {
            console.error("failed to save document")
        }
    }

    useEffect(() => {
        if (!currentHistory) return;

        const currentDoc = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id)
        if (selectedInnerDocument && currentDoc?.steps?.length > 0) {
            const found = currentDoc?.steps?.find(doc => doc?.sourceId === selectedInnerDocument?._id)
            const currentInnerDoc = { _id: found?.sourceId, steps: found?.value?.steps || [] }

            const initialValues = initializeDocumentForm(currentInnerDoc, selectedInnerDocument)
            form.setFieldsValue(initialValues)

            return
        }

        const initialValues = initializeDocumentForm(currentDoc, selectedDocument)
        form.setFieldsValue(initialValues)
    }, [currentHistory, selectedDocument, form, selectedInnerDocument])

    return (
        <>
            {saveStatus !== "idle" && (
                <div
                    style={{
                        textAlign: "right",
                        color: saveStatus === "saving" ? "#888" : "green",
                        marginBottom: 8,
                        transition: "all 0.3s",
                    }}
                >
                    {saveStatus === "saving"
                        ? t("accountVerification.saving")
                        : t("accountVerification.saved")}
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                style={{maxWidth: 700, margin: "auto"}}
                onFinish={handleSaveDocument}
                onValuesChange={() => {
                    handleAutoSave();
                }}
            >
                {
                    renderFormFields(t, form, fields, currentHistory?.status, handleAutoSave, [], { layout: "layout_3" })
                }

                <Form.Item>
                    <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            onClick={() => {
                                goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document)
                            }}
                            disabled={isSaving}
                        >
                            Back
                        </Button>


                        {
                            !isPendingVerification
                            && (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={saveLoading}
                                    disabled={saveLoading}
                                >
                                    Save
                                </Button>
                            )
                        }

                    </Space>
                </Form.Item>
            </Form>
        </>

    )
}

function VerificationPending() {
    return (
        <div className={"flex mt-6"}>
            <div className={"mx-auto text-center"} style={{ maxWidth: 600 }}>
                <Space direction={"vertical"} align={"center"}>
                    <FireOutlined style={{ fontSize: 32 }} />
                    <div>
                        <h4>{"Your verification is currently under review."}</h4>
                        <p>We’ll notify you as soon as the process is complete.</p>
                    </div>
                </Space>
            </div>
        </div>
    )
}

function VerificationStatusScreen(props) {
    const { statusHistory, onTryAgain, onStartOver, userVerificationTiers } = props;
    const { t } = useTranslation();
    const status = statusHistory?.status;

    const formatLimit = (val) => {
        if (val === null || val === undefined) return "Unlimited";
        const { dailyMax, allTimeMax } = val;
        if (dailyMax > 0) return `${Number(dailyMax).toLocaleString()} USD Daily`;
        if (allTimeMax > 0 && allTimeMax !== -1) return `${Number(allTimeMax).toLocaleString()} USD`;
        return "Unlimited";
    };

    if (status === "verification_failed") {
        const tierId = statusHistory?.tierSetting?._id || statusHistory?.tierSetting;
        const tier = userVerificationTiers?.find(t => t._id === tierId);
        const accessRightsList = Object.entries(tier?.accessRights || {});
        const accessWithAmounts = accessRightsList.filter(([_, val]) => val !== null && val !== undefined);

        return (
            <div className="verification-status-wrap">
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Card className="verification-status-card" bordered={false}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <div className="verification-status-header">
                                <CloseCircleFilled className="verification-status-icon danger" />
                                <div>
                                    <div className="verification-status-title">Verification Unsuccessful</div>
                                    <div className="verification-status-subtitle">• Please restart your verification.</div>
                                </div>
                            </div>

                            <Row gutter={12} className="verification-status-actions">
                                <Col xs={24} sm={12}>
                                    <Button type="primary" block onClick={onTryAgain}>
                                        Try Again
                                    </Button>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Button block onClick={onStartOver}>
                                        Start from Beginning
                                    </Button>
                                </Col>
                            </Row>

                            <Row gutter={24} className="verification-help-links">
                                <Col>
                                    <Space size={6}>
                                        <CustomerServiceOutlined className="verification-help-icon" />
                                        <span className="verification-help-link-text">Need help?</span>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space size={6}>
                                        <FileTextOutlined className="verification-help-icon" />
                                        <span className="verification-help-link-text">Identity Verification FAQ</span>
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                    </Card>

                    {accessWithAmounts.length > 0 && (
                        <Card className="verification-limits-card" bordered={false}>
                            <h3 className="verification-limits-title">Account Limits After Verification</h3>
                            <Space direction="vertical" size={14} style={{ width: "100%" }}>
                                {accessWithAmounts.map(([module, val]) => (
                                    <Row key={module} justify="space-between" align="middle" className="verification-limit-row">
                                        <Space size={10}>
                                            <LockOutlined className="verification-limit-icon" />
                                            <span className="verification-limit-label">
                                                {t(`accountVerification.tierSettingAccessRight.${module}`, module.replace(/_/g, " "))}
                                            </span>
                                        </Space>
                                        <span className="verification-limit-value">{formatLimit(val)}</span>
                                    </Row>
                                ))}
                            </Space>
                        </Card>
                    )}
                </Space>
            </div>
        );
    }

    return <VerificationPending />;
}
